import * as vega from "vega";
import { compile } from "vega-lite";
import * as vegaThemes from "vega-themes";
import Papa from "papaparse";

import { PLUGIN_ID, RENDERER_TIMEOUT_MS } from "../src/constants";

// Define the types for incoming messages
export interface RenderGraphData {
  graphSpec: string;
  size: "small" | "medium" | "large";
  theme: string;
  datasets: Record<string, string>;
}

// Define message types for outgoing messages
export type RendererMessageType = "RENDER_COMPLETE" | "RENDERER_READY";

// Define the structure for the RENDER_COMPLETE message data
export interface RenderCompleteData {
  svg: string;
}

// Define the union type for all possible message data types
export type RendererMessageData = RenderCompleteData | undefined;

// Helper function to send messages back to the widget with improved type safety
function postMessageToWidget(message: {
  type: RendererMessageType;
  data?: RendererMessageData;
}) {
  parent.postMessage(
    {
      pluginMessage: message,
      pluginId: PLUGIN_ID,
    },
    "*"
  );
}

/**
 * Handles rendering a Vega/Vega-Lite graph based on the provided data
 * @param data The graph data including spec, size, theme, and datasets
 * @param onComplete Callback function called when rendering is complete with the resulting SVG
 * @param onError Callback function called when an error occurs during rendering
 * @param timeoutMs Timeout in milliseconds for the rendering process
 * @returns A function that can be called to cancel the rendering process
 */
export async function handleRenderGraphMessage(
  data: RenderGraphData,
  onComplete: (svg: string) => void,
  onError: (error: Error, errorSvg: string) => void,
  timeoutMs: number = RENDERER_TIMEOUT_MS
): Promise<() => void> {
  const { graphSpec, size, theme, datasets } = data;

  // Set a timeout for the rendering process
  const vegaRenderTimeoutId = setTimeout(() => {
    console.error(`Rendering timed out after ${timeoutMs / 1000} seconds`);
    // Generate an error SVG
    const errorSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="100">
      <rect width="200" height="100" fill="#f8f8f8" />
      <text x="10" y="30" font-family="sans-serif" font-size="12" fill="#ff0000">
        Rendering timed out
      </text>
      <text x="10" y="50" font-family="sans-serif" font-size="12" fill="#ff0000">
        Graph may be too complex
      </text>
      <text x="10" y="70" font-family="sans-serif" font-size="12" fill="#ff0000">
        Try simplifying your data
      </text>
    </svg>`;

    onError(new Error("Rendering timed out"), errorSvg);
  }, timeoutMs);

  try {
    // Parse the graph definition
    const parsedDef = JSON.parse(graphSpec);

    // Apply theme if specified
    const maybeTheme = theme in vegaThemes ? (vegaThemes as any)[theme] : null;

    // Compile the vega-lite spec to vega
    const compiled = maybeTheme
      ? compile(parsedDef, { config: maybeTheme })
      : compile(parsedDef);

    const cSpec = compiled.spec; // Avoid truncation

    // Resize based on size parameter
    let resizedSpec;
    if (size === "small") {
      resizedSpec = cSpec;
    } else if (size === "medium") {
      resizedSpec = {
        ...cSpec,
        width: (typeof cSpec?.width === "number" ? cSpec.width : 250) * 2,
        height: (typeof cSpec?.height === "number" ? cSpec.height : 250) * 2,
      };
    } else if (size === "large") {
      resizedSpec = {
        ...cSpec,
        width: (typeof cSpec?.width === "number" ? cSpec.width : 400) * 4,
        height: (typeof cSpec?.height === "number" ? cSpec.height : 400) * 4,
      };
    }

    // Parse the vega spec
    const vegaSpec = vega.parse(resizedSpec || cSpec);

    // Create a new vega view
    const vegaView = new vega.View(vegaSpec, { renderer: "none" });

    // Attach custom datasets
    for (const [name, dataString] of Object.entries(datasets)) {
      if (dataString.trimStart().startsWith("[")) {
        try {
          const parsedData = JSON.parse(dataString);
          vegaView.data(name, parsedData);
          continue;
        } catch (error) {
          console.error(error);
        }
      } else {
        // Try to parse as CSV
        try {
          const result = Papa.parse(dataString);
          const header = result.data[0] as string[];
          const rows = result.data.slice(1) as any[][];

          const parsedData = rows.map((row) => {
            const jsonRow: Record<string, any> = {};
            row.forEach((cell, i) => {
              jsonRow[header[i]] = cell;
            });
            return jsonRow;
          });

          vegaView.data(name, parsedData);
          continue;
        } catch (error) {
          console.error(error);
        }
      }

      console.warn("this dataset is not a CSV or a JSON");
    }

    // Generate the SVG and send it back to the widget
    const svg = await vegaView.toSVG();

    // Clear the timeout since rendering completed successfully
    clearTimeout(vegaRenderTimeoutId);

    onComplete(svg);
  } catch (error) {
    console.error("Error rendering graph:", error);
    // Generate an error SVG
    const errorSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="100">
      <rect width="200" height="100" fill="#f8f8f8" />
      <text x="10" y="50" font-family="sans-serif" font-size="12" fill="#ff0000">
        Error rendering graph
      </text>
    </svg>`;

    onError(
      error instanceof Error ? error : new Error(String(error)),
      errorSvg
    );
  }

  // Return a function to cancel the rendering process
  return () => clearTimeout(vegaRenderTimeoutId);
}

// Listen for messages from the widget
window.onmessage = async (event) => {
  if (
    event.data.pluginMessage &&
    event.data.pluginMessage.type === "RENDER_GRAPH"
  ) {
    const renderData = event.data.pluginMessage.data as RenderGraphData;

    handleRenderGraphMessage(
      renderData,
      (svg) => {
        postMessageToWidget({
          type: "RENDER_COMPLETE",
          data: { svg },
        });
      },
      (error, errorSvg) => {
        postMessageToWidget({
          type: "RENDER_COMPLETE",
          data: { svg: errorSvg },
        });
      }
    );
  }
};

console.log("Renderer is ready to accept messages");

// Notify that the renderer is ready
postMessageToWidget({
  type: "RENDERER_READY",
});

// Add a text to the page to indicate that the renderer is ready
const readyText = document.createElement("div");
readyText.textContent = "Renderer is ready to accept messages";
document.body.appendChild(readyText);
